import { Controller } from "@hotwired/stimulus"

// Posts the selected sick note document (AU-Bescheinigung) to the server's
// extract endpoint and fills the form fields with the suggestions returned by
// the AI extractor. Only fields the user has not touched yet are overwritten
// (initial values are snapshotted on connect, since date_from/date_to are
// prefilled with today by the controller).
//
// Wiring: `data-controller="sick-note-document-extraction"` on the form, plus
// `data-sick-note-document-extraction-url-value` pointing to the extract
// endpoint. The file input declares
// `data-action="change->sick-note-document-extraction#fileChanged"`.
//
// The extracted patient name is never written into the form — it is only
// compared against the selected employee(s) for a non-blocking warning.
export default class extends Controller {
  static values = { url: String, employeeName: String, mismatchMessage: String }
  static targets = [
    "fileInput",
    "dateFrom",
    "dateTo",
    "sickLeaveType",
    "employeeSelect",
    "indicator",
    "warning",
  ]

  connect() {
    this.initialValues = new Map()
    this.prefillableInputs().forEach((input) => this.initialValues.set(input, input.value))
    this.extractedName = null
    this.onEmployeeChange = () => this.checkEmployeeName()
    this.employeeSelectTargets.forEach((select) =>
      select.addEventListener("change", this.onEmployeeChange),
    )
  }

  disconnect() {
    this.employeeSelectTargets.forEach((select) =>
      select.removeEventListener("change", this.onEmployeeChange),
    )
    if (this.abortController) this.abortController.abort()
  }

  async fileChanged(event) {
    const file = event.target.files && event.target.files[0]
    if (!file || !this.urlValue) return

    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()

    const formData = new FormData()
    formData.append("document", file)

    this.showIndicator()
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
        credentials: "same-origin",
        body: formData,
        signal: this.abortController.signal,
      })
      if (!response.ok) return

      const payload = await response.json()
      this.applySuggestions(payload.suggestions || {})
    } catch (error) {
      if (error.name !== "AbortError") console.warn("Sick note extraction failed:", error)
    } finally {
      this.hideIndicator()
    }
  }

  applySuggestions(suggestions) {
    this.applyToInput(this.hasDateFromTarget ? this.dateFromTarget : null, suggestions.date_from)
    this.applyToInput(this.hasDateToTarget ? this.dateToTarget : null, suggestions.date_to)
    this.applyToSelect(
      this.hasSickLeaveTypeTarget ? this.sickLeaveTypeTarget : null,
      suggestions.sick_leave_type_id,
    )
    this.extractedName = suggestions.employee_name || null
    this.checkEmployeeName()
  }

  // Fills the input only while its value still equals the snapshot taken on
  // connect (i.e. the user has not edited it).
  //
  // The date targets are the hidden ISO field of a date-picker, which syncs its visible input
  // off this `change` event — so a plain assign-and-dispatch is all that is needed.
  applyToInput(input, value) {
    if (!input || value === null || value === undefined || value === "") return
    if (input.value !== this.initialValues.get(input)) return // never overwrite the user

    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
    this.initialValues.set(input, input.value)
  }

  applyToSelect(select, value) {
    if (!select || value === null || value === undefined || value === "") return
    if (select.value !== this.initialValues.get(select)) return // never overwrite the user
    if (!select.querySelector(`option[value="${value}"]`)) return // type not offered in this context

    select.value = value
    select.dispatchEvent(new Event("change", { bubbles: true }))
    this.initialValues.set(select, select.value)
  }

  checkEmployeeName() {
    if (!this.hasWarningTarget) return

    const candidates = this.candidateNames()
    if (!this.extractedName || candidates.length === 0) {
      this.warningTarget.classList.add("hidden")
      return
    }

    const matches = candidates.some((candidate) => this.namesMatch(this.extractedName, candidate))
    if (matches) {
      this.warningTarget.classList.add("hidden")
    } else {
      this.warningTarget.textContent = this.mismatchMessageValue.replace(
        "__NAME__",
        this.extractedName,
      )
      this.warningTarget.classList.remove("hidden")
    }
  }

  candidateNames() {
    const names = this.employeeSelectTargets.flatMap((select) =>
      Array.from(select.selectedOptions || []).map((option) => option.textContent),
    )
    if (this.employeeNameValue) names.push(this.employeeNameValue)
    return names.filter((name) => name && name.trim() !== "")
  }

  // Token-based comparison: AU documents print "Nachname, Vorname" while the
  // employee selects show "Vorname Nachname (Beruf)", so exact string equality
  // would never match. Strips parenthesized suffixes and compares name tokens.
  namesMatch(extracted, candidate) {
    const tokenize = (value) =>
      value
        .toLowerCase()
        .replace(/\(.*?\)/g, " ")
        .split(/[\s,.-]+/)
        .filter((token) => token.length > 1)
    const extractedTokens = tokenize(extracted)
    const candidateTokens = tokenize(candidate)
    if (extractedTokens.length === 0 || candidateTokens.length === 0) return true

    const overlap = extractedTokens.filter((token) => candidateTokens.includes(token))
    return overlap.length >= Math.min(2, extractedTokens.length, candidateTokens.length)
  }

  prefillableInputs() {
    return [
      this.hasDateFromTarget ? this.dateFromTarget : null,
      this.hasDateToTarget ? this.dateToTarget : null,
      this.hasSickLeaveTypeTarget ? this.sickLeaveTypeTarget : null,
    ].filter(Boolean)
  }

  showIndicator() {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.remove("hidden")
    this.toggleSubmit(true)
  }

  hideIndicator() {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.add("hidden")
    this.toggleSubmit(false)
  }

  toggleSubmit(disabled) {
    this.element.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((btn) => {
      btn.disabled = disabled
      btn.classList.toggle("opacity-50", disabled)
      btn.classList.toggle("cursor-not-allowed", disabled)
    })
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
